import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import Pagination from "../../components/ui/Pagination";
import BranchSelect from "../../components/ui/BranchSelect";
import {
  useEvaluationCounts,
  useEvaluations,
  useTemplates,
} from "../../api/queries/performanceQueries";
import { useDepartments } from "../../api/queries/organizationQueries";
import { DEFAULT_BANDS } from "./components/templateUtils";
import useCurrentUser from "../../hooks/useCurrentUser";
import { getApiErrorMessage } from "../../utils/apiError";
import { currentPeriod, formatPlainDate } from "../../utils/dateTime";
import { employeeName } from "../../utils/text";
import EvaluationStatusBadge from "./components/EvaluationStatusBadge";
import CreateEvaluationDialog from "./components/CreateEvaluationDialog";
import { useEvaluationDialogs } from "./components/EvaluationDialogs";
import { isOverdue } from "./components/evaluationUtils";
import "./performance.css";
import { useCanModify } from "../../hooks/usePermissions";
import usePermissions from "../../hooks/usePermissions";

const TABS = [
  { key: "", label: "All" },
  { key: "PENDING_EVALUATION", label: "For rating" },
  { key: "PENDING_SELF", label: "Self-assessment" },
  { key: "PENDING_ACKNOWLEDGEMENT", label: "Waiting for employee" },
  { key: "CONTESTED", label: "Contested – HR to resolve" },
  { key: "COMPLETED", label: "Completed" },
  { key: "CANCELLED", label: "Cancelled" },
];
const FILTER_KEYS = [
  "templateCode",
  "branchId",
  "year",
  "search",
  "departmentId",
  "ratingBand",
  "minScore",
  "maxScore",
  "employeeResponse",
];
const EMPTY = [];

/**
 * Row actions by status (list rows have no `permissions`; the detail page
 * re-checks). Branch managers only get View.
 */
const RowActions = ({ ev, isHr, onAction }) => {
  const navigate = useNavigate();
  const items = [];
  if (isHr) {
    if (ev.status === "PENDING_EVALUATION")
      items.push({
        key: "encode",
        label: "Encode ratings",
        icon: "bi-pencil-square",
      });
    if (ev.status === "PENDING_SELF" || ev.status === "PENDING_EVALUATION")
      items.push({
        key: "reassign",
        label: "Reassign evaluator",
        icon: "bi-arrow-left-right",
      });
  }
  if (ev.status === "COMPLETED" || ev.status === "CANCELLED")
    items.push({ key: "print", label: "Print", icon: "bi-printer" });
  if (isHr && ev.status !== "COMPLETED" && ev.status !== "CANCELLED")
    items.push({
      key: "cancel",
      label: "Cancel",
      icon: "bi-x-circle",
      danger: true,
    });

  const run = (key) => {
    if (key === "encode") navigate(`/performance/evaluations/${ev.id}?edit=1`);
    else if (key === "print")
      window.open(
        `/performance/evaluations/${ev.id}/print`,
        "_blank",
        "noopener",
      );
    else onAction(key, ev);
  };

  return (
    <div className="d-inline-flex gap-1" onClick={(e) => e.stopPropagation()}>
      <Link
        to={`/performance/evaluations/${ev.id}`}
        className="btn btn-sm btn-light border perf-action-btn"
      >
        View
      </Link>
      {items.length > 0 && (
        <div className="dropdown">
          <button
            type="button"
            className="btn btn-sm btn-light border rounded-6 px-2"
            data-bs-toggle="dropdown"
            data-bs-popper-config='{"strategy":"fixed"}'
            aria-expanded="false"
            aria-label={`More actions for ${employeeName(ev.employee)}`}
          >
            <i className="bi bi-three-dots"></i>
          </button>
          <ul className="dropdown-menu dropdown-menu-end shadow-sm fs-085">
            {items.map((it) => (
              <li key={it.key}>
                <button
                  type="button"
                  className={`dropdown-item ${it.danger ? "text-danger" : ""}`}
                  onClick={() => run(it.key)}
                >
                  <i className={`bi ${it.icon} me-2`}></i>
                  {it.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const EvaluationsPage = () => {
  // View-only roles (performance without modify) only browse and print
  const canModify = useCanModify("performance");
  const navigate = useNavigate();
  const user = useCurrentUser();
  // HR-level (performance with COMPANY scope); role names grant nothing
  const isCompanyWide = usePermissions().companyWide("performance");
  const [params, setParams] = useSearchParams();
  const status = TABS.some((t) => t.key === params.get("status"))
    ? params.get("status")
    : "";
  const page = Number(params.get("page")) || 1;
  const limit = Number(params.get("limit")) || 20;
  const filters = useMemo(
    () => Object.fromEntries(FILTER_KEYS.map((k) => [k, params.get(k) || ""])),
    [params],
  );
  const hasFilters = FILTER_KEYS.some((k) => filters[k]);

  const update = (changes, { resetPage = true } = {}) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) =>
      v ? next.set(k, v) : next.delete(k),
    );
    if (resetPage && !("page" in changes)) next.delete("page");
    setParams(next, { replace: true });
  };

  const [searchText, setSearchText] = useState(filters.search);
  useEffect(() => {
    const value = searchText.trim();
    if (value === filters.search) return undefined;
    const t = setTimeout(() => update({ search: value }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const [creating, setCreating] = useState(false);
  const { open, dialog } = useEvaluationDialogs();

  const list = useEvaluations({ ...filters, status, page, limit });
  const counts = useEvaluationCounts(filters);
  const templates = useTemplates();
  const { data: departments = EMPTY } = useDepartments(user.companyId);
  const rows = list.data?.data || EMPTY;
  const total = list.data?.total || 0;

  const templateCodes = useMemo(() => {
    const byCode = new Map();
    (templates.data || EMPTY).forEach((t) => {
      if (!byCode.has(t.code)) byCode.set(t.code, t.name);
    });
    return [...byCode.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [templates.data]);
  const thisYear = currentPeriod().year;
  const years = [thisYear, thisYear - 1, thisYear - 2, thisYear - 3];

  const countFor = (key) =>
    key ? counts.data?.byStatus?.[key] : counts.data?.total;

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <h1 className="fw-bold text-dark m-0 page-title">Evaluations</h1>
            <p className="text-muted small mb-0">
              Create, track and review performance evaluations.
            </p>
          </div>
          {isCompanyWide && (
            <div className="d-flex gap-2">
              <Link
                to="/performance/reports"
                className="btn btn-sm px-3 profile-btn-white btn h-35px d-flex align-items-center"
              >
                <i className="bi bi-bar-chart me-2"></i> Reports
              </Link>
              {canModify && (
                <>
                  <Link
                    to="/performance/evaluations/bulk"
                    className="btn btn-sm px-3 profile-btn-white btn h-35px d-flex align-items-center"
                  >
                    <i className="bi bi-people me-2"></i> Create for many
                  </Link>
                  <button
                    type="button"
                    className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
                    onClick={() => setCreating(true)}
                  >
                    <i className="bi bi-plus-lg me-2"></i> New Evaluation
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div
          className="approval-tabs"
          role="tablist"
          aria-label="Evaluation status"
        >
          {TABS.map((t) => {
            const n = countFor(t.key);
            return (
              <button
                key={t.key || "all"}
                type="button"
                role="tab"
                aria-selected={status === t.key}
                className={`approval-tab ${status === t.key ? "active" : ""}`}
                onClick={() => update({ status: t.key })}
              >
                {t.label}
                {n !== undefined && (
                  <span className="approval-tab-count approval-tab-count--plain">
                    {n}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="perf-filters mb-3">
          <div className="position-relative perf-filter-search">
            <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
            <input
              type="search"
              className="filter-input w-100 ps-5"
              placeholder="Name or employee number (starts with)"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              aria-label="Search name or employee number"
            />
          </div>
          <select
            className="status-select"
            value={filters.templateCode}
            onChange={(e) => update({ templateCode: e.target.value })}
            aria-label="Template"
          >
            <option value="">All forms</option>
            {templateCodes.map(([code, name]) => (
              <option key={code} value={code}>
                {code} · {name}
              </option>
            ))}
          </select>
          <div className="perf-filter-branch">
            <BranchSelect
              value={filters.branchId}
              onChange={(v) => update({ branchId: v })}
              id="eval-branch"
            />
          </div>
          <select
            className="status-select"
            value={filters.year}
            onChange={(e) => update({ year: e.target.value })}
            aria-label="Year (period end)"
          >
            <option value="">All years</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <select
            className="status-select"
            value={filters.departmentId}
            onChange={(e) => update({ departmentId: e.target.value })}
            aria-label="Department"
          >
            <option value="">All departments</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.departmentName}
              </option>
            ))}
          </select>
          <select
            className="status-select"
            value={filters.employeeResponse}
            onChange={(e) => update({ employeeResponse: e.target.value })}
            aria-label="Employee response"
          >
            <option value="">Any response</option>
            <option value="ACCEPT">Accepted</option>
            <option value="CONTEST">Contested</option>
          </select>
          <input
            className="filter-input perf-band-filter"
            list="perf-bands"
            value={filters.ratingBand}
            onChange={(e) => update({ ratingBand: e.target.value })}
            placeholder="Rating band"
            aria-label="Rating band"
          />
          <datalist id="perf-bands">
            {DEFAULT_BANDS.map((b) => (
              <option key={b.label} value={b.label} />
            ))}
          </datalist>
          <div className="d-flex align-items-center gap-1">
            <input
              type="number"
              className="filter-input perf-score-filter"
              min="0"
              max="100"
              value={filters.minScore}
              onChange={(e) => update({ minScore: e.target.value })}
              placeholder="Min score"
              aria-label="Minimum score"
            />
            <span className="text-slate-400">–</span>
            <input
              type="number"
              className="filter-input perf-score-filter"
              min="0"
              max="100"
              value={filters.maxScore}
              onChange={(e) => update({ maxScore: e.target.value })}
              placeholder="Max"
              aria-label="Maximum score"
            />
          </div>
          {hasFilters && (
            <button
              type="button"
              className="btn btn-sm btn-link text-slate-600"
              onClick={() => {
                setSearchText("");
                update(Object.fromEntries(FILTER_KEYS.map((k) => [k, ""])));
              }}
            >
              Clear filters
            </button>
          )}
          {list.isFetching && !list.isLoading && (
            <span
              className="spinner-border spinner-border-sm text-primary ms-auto"
              role="status"
              aria-label="Refreshing"
            ></span>
          )}
        </div>

        <div className="glass-table-card">
          {list.isError ? (
            <div className="approval-empty text-danger">
              <i className="bi bi-exclamation-triangle"></i>
              {getApiErrorMessage(list.error, "Could not load evaluations.")}
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover mb-0 perf-table">
                  <thead className="custom-table-header">
                    <tr>
                      <th className="ps-4 py-3">Employee</th>
                      <th className="py-3">Branch</th>
                      <th className="py-3">Form</th>
                      <th className="py-3">Period</th>
                      <th className="py-3">Evaluator</th>
                      <th className="py-3">Status</th>
                      <th className="py-3 text-end">Score</th>
                      <th className="py-3">Due</th>
                      <th className="py-3 pe-4 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="border-top-0">
                    {rows.map((ev) => {
                      const overdue = isOverdue(ev);
                      return (
                        <tr
                          key={ev.id}
                          className="cursor-pointer"
                          onClick={() =>
                            navigate(`/performance/evaluations/${ev.id}`)
                          }
                        >
                          <td className="ps-4">
                            <div className="emp-name-text">
                              {employeeName(ev.employee)}
                            </div>
                            <div className="emp-sub-text">
                              {ev.employee?.employeeId}
                            </div>
                          </td>
                          <td>{ev.employee?.branch?.branchName || "—"}</td>
                          <td>
                            {ev.template?.name}
                            <span className="text-slate-400 fs-070 ms-1">
                              v{ev.template?.version}
                            </span>
                          </td>
                          <td className="text-nowrap">{ev.periodLabel}</td>
                          <td>{employeeName(ev.evaluator)}</td>
                          <td>
                            <EvaluationStatusBadge evaluation={ev} />
                          </td>
                          <td className="text-end text-nowrap">
                            {ev.totalScore !== null &&
                            ev.totalScore !== undefined ? (
                              <>
                                <strong className="tabular-nums">
                                  {ev.totalScore}
                                </strong>
                                {ev.ratingBand && (
                                  <div className="fs-070 text-slate-500">
                                    {ev.ratingBand}
                                  </div>
                                )}
                              </>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td
                            className={`text-nowrap ${overdue ? "text-danger fw-semibold" : ""}`}
                          >
                            {ev.dueDate ? formatPlainDate(ev.dueDate) : "—"}
                            {overdue && <div className="fs-070">Overdue</div>}
                          </td>
                          <td className="pe-4 text-end text-nowrap">
                            <RowActions
                              ev={ev}
                              isHr={isCompanyWide && canModify}
                              onAction={open}
                            />
                          </td>
                        </tr>
                      );
                    })}
                    {!list.isLoading && rows.length === 0 && (
                      <tr>
                        <td colSpan={9} className="approval-empty">
                          <i className="bi bi-clipboard2-check"></i>
                          {hasFilters || status
                            ? "No evaluations match."
                            : "No evaluations yet."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {list.isLoading && (
                <div className="text-center py-5">
                  <span
                    className="spinner-border text-primary"
                    role="status"
                    aria-label="Loading"
                  ></span>
                </div>
              )}
              <Pagination
                page={page}
                limit={limit}
                total={total}
                onPageChange={(p) =>
                  update({ page: String(p) }, { resetPage: false })
                }
                onLimitChange={(l) => update({ limit: String(l) })}
              />
            </>
          )}
        </div>
      </div>

      {creating && (
        <CreateEvaluationDialog onClose={() => setCreating(false)} />
      )}
      {dialog}
    </Layout>
  );
};

export default EvaluationsPage;
